/**
 * Dictation error taxonomy and the transcription request itself.
 *
 * The request lives here rather than in the hook because every interesting decision it
 * makes -- which statuses are worth another tap, whether a retry re-uploads the audio,
 * whether a retry bypasses Clerk's token cache -- has to be provable, and `client/` has
 * no test runner by rule. All I/O is injected, so this file stays framework-free and
 * the hook supplies the real Clerk, Apollo, XHR and fetch.
 */

export type DictationErrorCode = 'permission-denied' | 'unavailable' | 'unsupported' | 'failed';

/** Codes worth another tap. The rest mean the service itself is refusing. */
const RETRYABLE: ReadonlySet<DictationErrorCode> = new Set<DictationErrorCode>(['permission-denied', 'failed']);

const MESSAGE_KEYS: Record<DictationErrorCode, string> = {
  'permission-denied': 'input.dictation-permission-denied',
  unavailable: 'input.dictation-unavailable',
  unsupported: 'input.dictation-unsupported',
  failed: 'input.dictation-failed',
};

export class DictationError extends Error {
  code: DictationErrorCode;

  constructor(code: DictationErrorCode, message: string) {
    super(message);
    this.code = code;
  }
}

export function isRetryable(code: DictationErrorCode) {
  return RETRYABLE.has(code);
}

/** i18n key for a code, in the `chat` namespace. Every surface shows the same message. */
export function dictationMessageKey(code: DictationErrorCode) {
  return MESSAGE_KEYS[code];
}

/**
 * A 401 is the lambda refusing this token, and a Clerk session token lives 60 seconds:
 * one that was minted before a slow signed-URL round trip can arrive past its own `exp`.
 * That is a timing blip, so it stays retryable and the mic stays usable. A 429 or a 5xx
 * is the same shape of answer for a different reason -- the lambda resolves the caller's
 * `asfId` through Clerk when the session token carries no claim, and it answers 503
 * `auth_unavailable` when Clerk cannot be reached -- so those are worth another tap too.
 * A 403 is the lambda refusing this *caller*, whose audio key is not under their `asfId`,
 * which no number of taps will change: it latches, and so does anything else
 * unrecognised.
 */
export function classifyLambdaStatus(status: number): DictationErrorCode {
  if (status === 401 || status === 429 || status >= 500) {
    return 'failed';
  }
  return 'unavailable';
}

/**
 * Operator-facing detail. The three refusals want three different people looking: a 401
 * is the token, a 403 is the S3 key the signed-URL resolver built (it verified the token
 * fine and then found the object outside the caller's `asfId` prefix), and anything else
 * is the lambda itself.
 */
export function lambdaFailureDetail(status: number) {
  if (status === 401) {
    return 'rejected the session token';
  }
  if (status === 403) {
    return "rejected the audio key as not the caller's";
  }
  return 'failed';
}

/**
 * What survives one take across its retries. The hook holds exactly one of these per
 * recording and hands the same object to every attempt, so a retry can re-post audio
 * that is already in S3 instead of paying to store it twice.
 */
export interface DictationUpload {
  /** Where a successful PUT put the audio, or null while nothing has been uploaded. */
  audioUrl: string | null;
  /** The last POST was refused as 401, so the next token must not come from the cache. */
  authFailed: boolean;
}

export function newUpload(): DictationUpload {
  return { audioUrl: null, authFailed: false };
}

/** The shape of `fetch`'s Response that this module actually reads. */
export interface TranscriptionResponse {
  ok: boolean;
  status: number;
  statusText: string;
  json: () => Promise<unknown>;
}

export interface TranscribeDeps<TAudio> {
  endpoint: string;
  audio: TAudio;
  /** Clerk's `getToken`. `skipCache` forces a freshly minted token. */
  getToken: (options: { skipCache: boolean }) => Promise<string | null>;
  /** Puts the audio in S3 and resolves its URL. Only ever called when there is none. */
  upload: (audio: TAudio) => Promise<string>;
  post: (
    endpoint: string,
    init: { method: string; headers: Record<string, string>; body: string }
  ) => Promise<TranscriptionResponse>;
}

/**
 * A session token the lambda can verify. A gap here is transient: report it as failed so
 * the next tap can succeed, never as the latching unavailable.
 *
 * `skipCache` is passed only after a 401. Clerk's cache can hand back the very token the
 * lambda just refused, which would make the retry fail identically and trap the artist in
 * a loop; minting on every call instead would add a Clerk round trip to every dictation
 * and break it outright whenever Clerk's API is the thing having a bad minute.
 */
async function readToken(getToken: TranscribeDeps<unknown>['getToken'], skipCache: boolean) {
  try {
    const token = await getToken({ skipCache });
    if (!token) {
      throw new Error('Clerk returned no session token');
    }
    return token;
  } catch (error) {
    console.error('Error fetching dictation session token:', error);
    throw new DictationError('failed', 'Could not obtain a session token');
  }
}

/**
 * Uploads the audio if it is not already in S3, posts it for transcription, and returns
 * the text. Throws `DictationError`; `upload` mutates `session` so a later attempt with
 * the same object skips straight to the POST.
 */
export async function transcribe<TAudio>(session: DictationUpload, deps: TranscribeDeps<TAudio>): Promise<string> {
  // Gate before the signed URL is minted: a run that cannot authenticate must not upload
  // at all, because the lambda would refuse the audio it just paid to store. Cached is
  // enough here -- this only asks whether there is a session, and the read that has to
  // survive the round trip below is the one right before the POST.
  await readToken(deps.getToken, false);

  if (!session.audioUrl) {
    session.audioUrl = await deps.upload(deps.audio);
  }

  // Re-read rather than reusing the token from the gate: the signed-URL round trip and
  // the upload of a long recording can outlive it.
  const token = await readToken(deps.getToken, session.authFailed);

  const response = await deps.post(deps.endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ url: session.audioUrl }),
  });

  if (!response.ok) {
    session.authFailed = response.status === 401;
    throw new DictationError(
      classifyLambdaStatus(response.status),
      `Transcription ${lambdaFailureDetail(response.status)}: ${response.status} ${response.statusText}`
    );
  }

  session.authFailed = false;

  const body = (await response.json()) as { transcription?: unknown };
  const text = typeof body?.transcription === 'string' ? body.transcription.trim() : '';

  if (!text) {
    throw new DictationError('failed', 'Transcription came back empty');
  }

  return text;
}
